import React, { useState, useRef, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/@/components/ui/tooltip';

const FALLBACK_COLUMNS = [
  { id: 'A Fazer', label: 'A Fazer', cor: '#6b7280' },
  { id: 'Em Progresso', label: 'Em Progresso', cor: '#3b82f6' },
  { id: 'Concluído', label: 'Concluído', cor: '#22c55e' },
];

const PRIORITY_CONFIG = {
  low: { label: 'Baixa', color: '#9ca3af', bg: 'bg-gray-100 dark:bg-gray-500/10' },
  medium: { label: 'Média', color: '#f59e0b', bg: 'bg-amber-100 dark:bg-amber-500/10' },
  high: { label: 'Alta', color: '#f97316', bg: 'bg-orange-100 dark:bg-orange-500/10' },
  urgent: { label: 'Urgente', color: '#ef4444', bg: 'bg-red-100 dark:bg-red-500/10' },
};

const PRIORITY_ICON = {
  low: 'mdi:arrow-down',
  medium: 'mdi:minus',
  high: 'mdi:arrow-up',
  urgent: 'mdi:alert-circle',
};

const TAG_COLORS = [
  'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
  'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400',
];

function getTagColor(index) {
  return TAG_COLORS[index % TAG_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function hashStringToColor(str) {
  if (!str) return '#7C3AED';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6'];
  return colors[Math.abs(hash) % colors.length];
}

function formatFullDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) +
    ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function KanbanBoard({ tasks, onStatusChange, onEdit, workflow, usuariosList = [] }) {
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragItem = useRef(null);

  const columns = useMemo(() => {
    if (workflow?.estagios?.length > 0) {
      return [...workflow.estagios]
        .sort((a, b) => a.ordem - b.ordem)
        .map(e => ({ id: e.nome, label: e.nome, cor: e.cor }));
    }
    return FALLBACK_COLUMNS;
  }, [workflow]);

  const handleDragStart = (e, task) => {
    dragItem.current = task;
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragOverColumn(null);
    dragItem.current = null;
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    const task = dragItem.current;
    if (task && task.status !== columnId) {
      onStatusChange(task._id, columnId);
    }
    dragItem.current = null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === today.toDateString()) return 'Hoje';
    if (d.toDateString() === tomorrow.toDateString()) return 'Amanhã';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date() && new Date(dateString).toDateString() !== new Date().toDateString();
  };

  const isDueSoon = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    const diff = d - now;
    return diff > 0 && diff < 2 * 24 * 60 * 60 * 1000;
  };

  const lastColumnId = columns[columns.length - 1]?.id;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="grid grid-cols-1 gap-4 flex-1 min-h-0"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(280px, 1fr))` }}
      >
        {columns.map(col => {
          const colTasks = tasks.filter(t => t.status === col.id);
          const isDragOver = dragOverColumn === col.id;

          return (
            <div
              key={col.id}
              className={`rounded-2xl bg-gray-100/80 dark:bg-[#1A1D21]/60 border border-gray-200/60 dark:border-[#383838]/40 overflow-hidden flex flex-col transition-all min-h-0 ${isDragOver ? 'ring-2 ring-[#7C3AED]/30 bg-[#7C3AED]/[0.03]' : ''}`}
              onDragOver={e => handleDragOver(e, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, col.id)}
            >
              {/* Column Header */}
              <div className="px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <span
                    className="w-3 h-3 rounded-full ring-4 ring-opacity-20"
                    style={{
                      backgroundColor: col.cor,
                      boxShadow: `0 0 0 4px ${col.cor}20`,
                    }}
                  />
                  <span className="text-[13px] font-bold text-gray-700 dark:text-[#D1D2D3] tracking-tight">{col.label}</span>
                </div>
                <span
                  className="text-[11px] font-bold min-w-[22px] h-[22px] flex items-center justify-center rounded-full"
                  style={{
                    backgroundColor: `${col.cor}15`,
                    color: col.cor,
                  }}
                >
                  {colTasks.length}
                </span>
              </div>

              {/* Cards Area */}
              <div className={`px-3 pb-3 flex-1 min-h-0 overflow-y-auto space-y-2 transition-colors ${isDragOver ? 'bg-[#7C3AED]/[0.02]' : ''}`}>
                {colTasks.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-28 text-gray-300 dark:text-[#ABABAD]/30">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#222529] flex items-center justify-center mb-2">
                      <Iconify icon="mdi:cards-outline" width={20} className="text-gray-300 dark:text-[#ABABAD]/30" />
                    </div>
                    <span className="text-[11px] font-medium">Arraste tarefas aqui</span>
                  </div>
                )}
                {colTasks.map(task => {
                  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  const priorityIcon = PRIORITY_ICON[task.priority] || PRIORITY_ICON.medium;
                  const overdue = isOverdue(task.due_date) && task.status !== lastColumnId;
                  const dueSoon = !overdue && isDueSoon(task.due_date) && task.status !== lastColumnId;
                  const assignedId = task.assigned_to?._id || task.assigned_to;
                  const listUser = usuariosList.find(u => u._id === assignedId);
                  const assignedName = task.assigned_to?.nomeFantasia || listUser?.nomeFantasia || task.assigned_to?.nome || listUser?.nome || task.assigned_to?.email || listUser?.email || null;
                  const avatarColor = hashStringToColor(assignedName || task.assigned_to?._id);
                  const checklist = task.checklist || [];
                  const checklistDone = checklist.filter(c => c.done).length;
                  const checklistTotal = checklist.length;
                  const tags = task.tags || [];
                  const attachments = task.attachments || [];
                  const hasDescription = !!task.description;

                  return (
                    <div
                      key={task._id}
                      draggable
                      onDragStart={e => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => onEdit(task)}
                      className="bg-white dark:bg-[#222529] rounded-xl border border-gray-200 dark:border-[#383838] shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-[#4a4a4a] transition-all group relative cursor-grab active:cursor-grabbing"
                    >
                      {/* Card color indicator */}
                      {task.card_color && (
                        <div
                          className="w-full h-1.5 rounded-t-xl"
                          style={{ backgroundColor: task.card_color }}
                        />
                      )}

                      <div className="px-3.5 py-3">
                        {/* Top row: Priority badge + Due date */}
                        <div className="flex items-center justify-between mb-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider"
                                style={{ backgroundColor: `${priority.color}15`, color: priority.color }}
                              >
                                <Iconify icon={priorityIcon} width={11} />
                                {priority.label}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">Prioridade: <strong>{priority.label}</strong></p>
                            </TooltipContent>
                          </Tooltip>

                          {task.due_date && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`text-[10px] font-semibold flex items-center gap-1 px-1.5 py-0.5 rounded-md ${
                                    overdue
                                      ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                                      : dueSoon
                                      ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400'
                                      : 'bg-gray-50 text-gray-500 dark:bg-[#1A1D21] dark:text-[#ABABAD]'
                                  }`}
                                >
                                  <Iconify icon={overdue ? 'mdi:alert-circle-outline' : 'mdi:calendar-outline'} width={11} />
                                  {formatDate(task.due_date)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="text-xs">
                                  {overdue ? '⚠ Atrasada — ' : 'Vencimento: '}
                                  <strong>{formatFullDate(task.due_date)}</strong>
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>

                        {/* Title */}
                        <p className="text-[13px] font-semibold text-gray-900 dark:text-[#D1D2D3] leading-snug line-clamp-2 mb-2">
                          {task.title}
                        </p>

                        {/* Description preview */}
                        {hasDescription && (
                          <p className="text-[11px] text-gray-400 dark:text-[#ABABAD]/70 line-clamp-1 mb-2">
                            {task.description}
                          </p>
                        )}

                        {/* Tags */}
                        {tags.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap mb-2">
                            {tags.slice(0, 3).map((tag, i) => (
                              <span
                                key={tag}
                                className={`px-1.5 py-px text-[9px] font-bold uppercase tracking-wider rounded-md ${getTagColor(i)}`}
                              >
                                {tag}
                              </span>
                            ))}
                            {tags.length > 3 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-[9px] font-bold text-gray-400 dark:text-[#ABABAD] cursor-default">
                                    +{tags.length - 3}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p className="text-xs">{tags.slice(3).join(', ')}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        )}

                        {/* Checklist progress */}
                        {checklistTotal > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="mb-2">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-[10px] font-medium text-gray-400 dark:text-[#ABABAD] flex items-center gap-1">
                                    <Iconify icon="mdi:checkbox-marked-outline" width={11} />
                                    {checklistDone}/{checklistTotal}
                                  </span>
                                  <span className="text-[10px] font-medium text-gray-400 dark:text-[#ABABAD]">
                                    {Math.round((checklistDone / checklistTotal) * 100)}%
                                  </span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-100 dark:bg-[#383838] rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                      width: `${(checklistDone / checklistTotal) * 100}%`,
                                      backgroundColor: checklistDone === checklistTotal ? '#22c55e' : '#7C3AED',
                                    }}
                                  />
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">
                                Checklist: <strong>{checklistDone}</strong> de <strong>{checklistTotal}</strong> itens concluídos
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {/* Bottom row: Assignee + Meta icons */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-[#383838]/50">
                          {/* Assignee */}
                          {assignedName ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0 ring-2 ring-white dark:ring-[#222529]"
                                    style={{ backgroundColor: avatarColor }}
                                  >
                                    {getInitials(assignedName)}
                                  </div>
                                  <span className="text-[11px] font-medium text-gray-600 dark:text-[#ABABAD] truncate max-w-[120px]">
                                    {assignedName}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p className="text-xs">Responsável: <strong>{assignedName}</strong></p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-[#383838] flex items-center justify-center border border-dashed border-gray-200 dark:border-[#4a4a4a]">
                                    <Iconify icon="mdi:account-plus-outline" width={12} className="text-gray-300 dark:text-[#ABABAD]/40" />
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p className="text-xs">Nenhum responsável atribuído</p>
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {/* Meta icons */}
                          <div className="flex items-center gap-2">
                            {hasDescription && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-gray-300 dark:text-[#ABABAD]/40 hover:text-gray-500 dark:hover:text-[#ABABAD] transition-colors">
                                    <Iconify icon="mdi:text-box-outline" width={14} />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  <p className="text-xs">Possui descrição</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {attachments.length > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-gray-300 dark:text-[#ABABAD]/40 hover:text-gray-500 dark:hover:text-[#ABABAD] transition-colors flex items-center gap-0.5">
                                    <Iconify icon="mdi:paperclip" width={14} />
                                    <span className="text-[9px] font-bold">{attachments.length}</span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  <p className="text-xs">{attachments.length} anexo{attachments.length > 1 ? 's' : ''}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {checklistTotal > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={`flex items-center gap-0.5 text-[10px] font-bold ${checklistDone === checklistTotal ? 'text-green-500' : 'text-gray-300 dark:text-[#ABABAD]/40'}`}>
                                    <Iconify icon="mdi:checkbox-marked-circle-outline" width={14} />
                                    <span>{checklistDone}/{checklistTotal}</span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  <p className="text-xs">
                                    {checklistDone === checklistTotal ? 'Checklist completa!' : `${checklistDone} de ${checklistTotal} itens concluídos`}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
